import { useState, useEffect, useRef, FC } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ReactComponent as BinanceIcon } from "./icons/binance-icon.svg";
import { ReactComponent as BybitIcon } from "./icons/bybit-icon.svg";
import { ReactComponent as BingxIcon } from "./icons/bingx-icon.svg";
import { ReactComponent as MexcIcon } from "./icons/mexc-icon.svg";
import { ReactComponent as OkxIcon } from "./icons/okx-icon.svg";
import { Box, BoxProps } from "@mui/material";

const OrbitingLogos: FC<BoxProps> = () => {
  const [step, setStep] = useState(0);
  const [animating, setAnimating] = useState(true);
  const [radiusPhase, setRadiusPhase] = useState("contracting");
  const animationRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const logos = [
    { icon: <BybitIcon width={70} height={70} />, initialAngle: -17 },
    { icon: <BingxIcon />, initialAngle: 55 },
    { icon: <BinanceIcon />, initialAngle: 127 },
    { icon: <MexcIcon />, initialAngle: 199 },
    { icon: <OkxIcon />, initialAngle: 271 },
  ];

  const maxRadius = 75;
  const minRadius = 65;
  const stepDuration = 1.5;
  const radiusDuration = stepDuration / 2;
  const pauseDuration = 1;

  useEffect(() => {
    if (animating) {
      setRadiusPhase("contracting");

      animationRef.current = setTimeout(() => {
        setRadiusPhase("expanding");
      }, radiusDuration * 1000);

      return () => {
        if (animationRef.current) clearTimeout(animationRef.current);
      };
    } else {
      animationRef.current = setTimeout(() => {
        setStep((prev) => prev + 1);
        setAnimating(true);
      }, pauseDuration * 1000);

      return () => {
        if (animationRef.current) clearTimeout(animationRef.current);
      };
    }
  }, [animating, radiusDuration, pauseDuration]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "400px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      <AnimatePresence>
        <motion.div
          animate={{ rotate: step * 72 }}
          transition={{
            duration: stepDuration,
            ease: "easeInOut",
            onComplete: () => {
              setAnimating(false);
            },
          }}
          style={{
            width: maxRadius * 2 + 60,
            height: maxRadius * 2 + 60,
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {logos.map((logo, index) => {
            const angle = logo.initialAngle;
            const angleInRadians = (angle * Math.PI) / 180;

            const targetRadius = radiusPhase === "contracting" ? minRadius : maxRadius;

            return (
              <motion.div
                key={index}
                initial={{
                  x: Math.cos(angleInRadians) * maxRadius,
                  y: Math.sin(angleInRadians) * maxRadius,
                }}
                animate={{
                  x: Math.cos(angleInRadians) * targetRadius,
                  y: Math.sin(angleInRadians) * targetRadius,
                }}
                transition={{
                  duration: radiusDuration,
                  ease: "easeInOut",
                }}
                style={{
                  position: "absolute",
                  width: "80px",
                  height: "80px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <motion.div
                  animate={{ rotate: -step * 72 }}
                  transition={{
                    duration: stepDuration,
                    ease: "easeInOut",
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {logo.icon}
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};

export default OrbitingLogos;
